import { ChildType, NodeVariableTypes } from "@common/types/tree-types";
import { generateRandomNsKey } from "../../../../common/generate-random-key";
import copy from 'fast-copy'

interface OurNodeAttributesDataTypeParams {
  type_node_id: string;
  type_code: number;
}

interface OurNodeAttributesUnitParams {
  code: number;
  display_name: string;
  description: string;
}

interface OurNodeAttributesParams {
  data_acquisition_type: string;
  sample_interval_in_seconds: number;
  node_class: number;
  data_type: OurNodeAttributesDataTypeParams | null;
  unit: OurNodeAttributesUnitParams | null;
  last_known_value: number;
  last_known_value_timestamp: string;
  data_type_as_string: string;
  node_class_as_string: string;
}
interface OurNodeParams {
  key?: string;
  id?: string;
  name: string;
  node_type?: string;
  attributes?: OurNodeAttributesParams;
  selected?: boolean;
  visible?: boolean;
  collapsed?: boolean;
  transfered?: boolean;
  neighborAbove?: OurNode | null;
  neighborBelow?: OurNode | null;
  parent?: OurNode | null;
  updateChildren?: boolean;
  source_type?: string;
}
export class OurNode {
  key: string;
  id: string;
  name: string;
  node_type: string;
  attributes: OurNodeAttributesParams;
  source_type: string;
  selected: boolean;
  visible: boolean;
  collapsed: boolean;
  transfered: boolean;
  neighborAbove: OurNode | null;
  neighborBelow: OurNode | null;
  parent: OurNode | null;
  updateChildren: boolean;
  children: OurNode[];

  constructor({
    key,
    id,
    name,
    node_type,
    attributes,
    source_type = null,
    selected = false,
    visible = true,
    collapsed = false,
    transfered = false,
    neighborAbove = null,
    neighborBelow = null,
    parent = null,
    updateChildren = true,
  }: OurNodeParams) {
    this.key = key ? key : this.generateRandomKey();
    this.id = id ? id : this.generateRandomKey();
    this.name = name;
    this.node_type = node_type;
    this.attributes = attributes;
    this.source_type = source_type;
    this.selected = selected;
    this.visible = visible;
    this.collapsed = collapsed;
    this.transfered = transfered;
    this.neighborAbove = neighborAbove;
    this.neighborBelow = neighborBelow;
    this.parent = parent;
    this.updateChildren = updateChildren;
    this.children = [];
    if (parent && updateChildren) {
      parent.children.push(this);
    }
  }

  generateRandomKey(): string {
    return generateRandomNsKey();
  }

  selectYourselfAndAllChildren(callback?: (node: OurNode) => void): void {
    if (!this.transfered) {
      this.selected = true;
      if (callback) {
        callback(this);
      }
    }
    this.children.forEach((child) =>
      child.selectYourselfAndAllChildren(callback),
    );
  }

  deselectYourselfAndAllChildren(callback?: (node: OurNode) => void): void {
    this.selected = false;
    if (callback) {
      callback(this);
    }
    this.children.forEach((child) =>
      child.deselectYourselfAndAllChildren(callback),
    );
  }

  toggleSelected(): void {
    this.selected = !this.selected;
  }

  returnRootNodeFromOurNode(): OurNode {
    const traverse = (node: OurNode): OurNode => {
      if (node === null) {
        return node;
      }

      if (node.parent !== null && node.parent !== undefined) {
        return traverse(node.parent);
      }
      return node;
    };

    return traverse(this);
  }

  getSerializableRootNodeFromOurNode = (): ChildType | NodeVariableTypes => {
    const isVariableNode = this.node_type === "VARIABLE";

    if (isVariableNode) {
      // type safe check because OurNode is expecting a string while NodeVariableTypes expects a union type
      const safeAcquisitionType =
        this.attributes?.data_acquisition_type === "POLLING" ||
        this.attributes?.data_acquisition_type === "MONITORING"
          ? this.attributes.data_acquisition_type
          : "POLLING";

      return {
        id: this.id,
        key: this.key,
        name: this.name,
        selected: this.selected,
        node_type: this.node_type,
        expanded: this.collapsed === false,
        transferred: this.transfered,
        attributes: {
          ...this.attributes,
          data_acquisition_type: safeAcquisitionType,
        },
      };
    } else {
      return {
        id: this.id,
        key: this.key,
        name: this.name,
        selected: this.selected,
        node_type: this.node_type,
        source_type: this.source_type,
        expanded: this.collapsed === false,
        transferred: this.transfered,
        children:
          this.children?.map((child) =>
            child.getSerializableRootNodeFromOurNode(),
          ) ?? [],
      };
    }
  };

  toParentObjectTree(): object {
    return {
      key: this.key,
      id: this.id,
      name: this.name,
      selected: this.selected,
      visible: this.visible,
      collapsed: this.collapsed,
      parent: this.parent ? this.parent.toParentObjectTree() : null,
    };
  }

  toChildrenObjectTree(): object {
    return {
      key: this.key,
      id: this.id,
      name: this.name,
      selected: this.selected,
      visible: this.visible,
      collapsed: this.collapsed,
      children: this.children.map((child) => child.toChildrenObjectTree()),
    };
  }

  toServerChildrenObjectTree(): object {
    const buildFolderNodeObject = (node: OurNode): object => {
      const buildChildrenArray = (children: OurNode[]): object[] => {
        return children.map((child) => child.toServerChildrenObjectTree());
      };

      const buildObjectWithSourceType = (node: OurNode): object => {
        return {
          id: node.id,
          name: node.name,
          node_type: node.node_type,
          source_type: node.source_type,
          children: buildChildrenArray(node.children),
        };
      };

      const buildObjectWithOutSourceType = (node: OurNode): object => {
        return {
          id: node.id,
          name: node.name,
          node_type: node.node_type,
          children: buildChildrenArray(node.children),
        };
      };

      if (node.source_type) {
        return buildObjectWithSourceType(node);
      } else {
        return buildObjectWithOutSourceType(node);
      }
    };

    const buildDataNodeObject = (node: OurNode): object => {
      return {
        id: node.id,
        name: node.name,
        node_type: node.node_type,
        attributes: node.attributes,
      };
    };

    if (this.node_type.toLowerCase() === "folder") {
      return buildFolderNodeObject(this);
    } else {
      return buildDataNodeObject(this);
    }
  }

  toString(): string {
    return JSON.stringify(this.toParentObjectTree(), null, 0);
  }

  static childrenObjectTreeToFlatList(object: any): OurNode[] {
    const flatList: OurNode[] = [];
    let lastNodeAtSameLevel: OurNode | null = null;

    function traverse(node: any, parent: OurNode | null): void {
      const newNode = new OurNode({
        key: node.key,
        id: node.id,
        name: node.name,
        selected: node.selected,
        visible: node.visible,
        collapsed: node.collapsed,
        neighborAbove: null,
        neighborBelow: null,
        parent: parent,
        updateChildren: true,
      });
      flatList.push(newNode);

      // Set the neighbor relations
      if (lastNodeAtSameLevel) {
        lastNodeAtSameLevel.neighborBelow = newNode;
        newNode.neighborAbove = lastNodeAtSameLevel;
      }
      lastNodeAtSameLevel = newNode;

      const previousNodeAtThisLevel = lastNodeAtSameLevel;

      // Traverse children nodes
      node.children.forEach((child: any, index: number) => {
        if (index === 0) {
          lastNodeAtSameLevel = null; // reset for the first child of a new level
        }
        traverse(child, newNode);
      });

      // Restore last node for siblings level
      lastNodeAtSameLevel = previousNodeAtThisLevel;
    }

    traverse(object, null);
    return flatList;
  }

  static childrenObjectTreeToFlatSet(object: any): Set<OurNode> {
    const flatSet: Set<OurNode> = new Set();
    let lastNodeAtSameLevel: OurNode | null = null;

    function traverse(node: any, parent: OurNode | null): void {
      const newNode = new OurNode({
        key: node.key,
        id: node.id,
        name: node.name,
        selected: node.selected,
        visible: node.visible,
        collapsed: node.collapsed,
        neighborAbove: null,
        neighborBelow: null,
        parent: parent,
        updateChildren: true,
      });
      flatSet.add(newNode);

      // Set the neighbor relations
      if (lastNodeAtSameLevel) {
        lastNodeAtSameLevel.neighborBelow = newNode;
        newNode.neighborAbove = lastNodeAtSameLevel;
      }
      lastNodeAtSameLevel = newNode;

      const previousNodeAtThisLevel = lastNodeAtSameLevel;

      // Traverse children nodes
      node.children.forEach((child: any, index: number) => {
        if (index === 0) {
          lastNodeAtSameLevel = null; // reset for the first child of a new level
        }
        traverse(child, newNode);
      });

      // Restore last node for siblings level
      lastNodeAtSameLevel = previousNodeAtThisLevel;
    }

    traverse(object, null);
    return flatSet;
  }

  // this is still unsure if we go this route.
  static createOurNodeSetFromJson(jsonString: string): Set<OurNode> {
    const objects = JSON.parse(jsonString);

    const nodesMap = new Map<string, OurNode>(); // To store nodes temporarily by key
    const rootNodes = new Set<OurNode>(); // To store root nodes

    // First pass: Create nodes without setting up parent-child relationships
    objects.forEach((obj: any) => {
      const newNode = new OurNode({
        key: obj.key,
        id: obj.id,
        name: obj.name,
        selected: obj.selected,
        visible: obj.visible,
        collapsed: obj.collapsed,
        neighborAbove: null,
        neighborBelow: null,
        parent: null,
        updateChildren: true,
      });
      nodesMap.set(obj.key, newNode); // Store node by its key
    });

    // Second pass: Set parent-child relationships
    objects.forEach((obj: any) => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const currentNode = nodesMap.get(obj.key)!; // Get the current node

      if (obj.parentKey) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const parent = nodesMap.get(obj.parentKey)!; // Get parent node
        if (parent) {
          currentNode.parent = parent; // Set parent reference
          parent.children.push(currentNode); // Add current node as child
        }
      } else {
        rootNodes.add(currentNode); // No parentKey means it's a root node
      }
    });

    return rootNodes;
  }

  static filterNodesByName(inputSet: Set<OurNode>, name: string): Set<OurNode> {
    // implement this
    return new Set(
      Array.from(inputSet).filter((node: OurNode) => node.name.includes(name)),
    );
  }

  static renameNode(
    inputSet: Set<OurNode>,
    key: string,
    newName: string,
  ): Set<OurNode> {
    const setCopy = [...Array.from(inputSet)];

    for (let i = 0; i < setCopy.length; i++) {
      if (setCopy[i].key === key) {
        setCopy[i].name = newName;
        break;
      }
    }

    return new Set(setCopy);
  }

  toggleNodeCollapse(): void {
    this.collapsed = !this.collapsed;
    this.children.forEach((child) => child.toggleNodeVisibility());
  }

  toggleNodeVisibility(): void {
    this.visible = !this.visible;
    if (!this.collapsed) {
      this.children.forEach((child) => child.toggleNodeVisibility());
    }
  }

  createCopyWithoutNeighbors(): OurNode {
    return new OurNode({
      key: this.key,
      id: this.id,
      name: this.name,
      selected: this.selected,
      visible: this.visible,
      collapsed: this.collapsed,
      neighborAbove: null,
      neighborBelow: null,
      parent: null,
      updateChildren: false,
    });
  }

  static findAndDelete(array, key) {
    return array.filter((item) => item.key !== key);
  }

  static deleteNodeFromFlatList(list: Set<OurNode>, key: string) {
    const setCopy = [...Array.from(list)];

    const index = setCopy.findIndex((node) => node.key === key);
    const node = setCopy.find((node) => node.key === key);

    const parent = node?.parent;
    const neighborAbove = node?.neighborAbove;
    const neighborBelow = node?.neighborBelow;

    // update parent attribute on all children of the deleted node
    node.children &&
      node.children.forEach((child) => {
        child.parent = parent;
      });

    // update neighborAbove for the first child
    if (node.children.length > 0) {
      node.children[0].neighborAbove = neighborAbove ? neighborAbove : null;
    }

    // update neighborBelow for the last child
    if (node.children.length > 0) {
      node.children[node.children.length - 1].neighborBelow = neighborBelow
        ? neighborBelow
        : null;
    }

    if (node.children.length > 0) {
      // update neighborAbove for the neighborBelow
      if (neighborBelow) {
        neighborBelow.neighborAbove = node.children[node.children.length - 1];
      }

      // update neighborBelow for the neighborAbove
      if (neighborAbove) {
        neighborAbove.neighborBelow = node.children[0];
      }
    } else {
      // update nodes neighborAbove and neighborBelow neighbors
      if (neighborAbove) {
        node.neighborAbove.neighborBelow = neighborBelow;
      }
      if (neighborBelow) {
        node.neighborBelow.neighborAbove = neighborAbove;
      }
    }

    // find the index of the node in the parent's children array
    const nodeIndex = parent.children.indexOf(node);

    // update the parents children
    parent.children.splice(nodeIndex, 1, ...node.children);

    node.parent = null;

    if (index !== -1) {
      setCopy.splice(index, 1);
    }
    return new Set(setCopy);
  }

  // TODO: make non static
  static insertNodeAfterTargetNodeInChildrenArray(children, targetNode, node) {
    const index = children.indexOf(targetNode);
    children.splice(index + 1, 0, node);
  }

  // TODO: make non static
  static appendNode(
    rootNode: OurNode,
    targetNode: OurNode,
    newFolderNode: OurNode,
  ) {
    const parent = targetNode.parent;
    const neighborBelow = targetNode.neighborBelow;
    targetNode.neighborBelow = newFolderNode;
    neighborBelow.neighborAbove = newFolderNode;
    newFolderNode.neighborAbove = targetNode;
    newFolderNode.neighborBelow = neighborBelow;

    OurNode.insertNodeAfterTargetNodeInChildrenArray(
      parent.children,
      targetNode,
      newFolderNode,
    );

    // TODO: return a new root node copy
    return OurNode.childrenObjectTreeToFlatSet(rootNode);
  }

  appendNode(newFolderNode: OurNode) {
    const parent = this.parent;
    const neighborBelow = this.neighborBelow;
    if (neighborBelow) {
      this.neighborBelow = newFolderNode;
      neighborBelow.neighborAbove = newFolderNode;
      newFolderNode.neighborAbove = this;
      newFolderNode.neighborBelow = neighborBelow;
    } else {
      this.neighborBelow = newFolderNode;
      newFolderNode.neighborAbove = this;
    }
    newFolderNode.parent = parent;
    OurNode.insertNodeAfterTargetNodeInChildrenArray(
      parent.children,
      this,
      newFolderNode,
    );
  }

  static insertNodeInChildrensArray(
    rootNode: OurNode,
    targetNode: OurNode,
    nodeToBeInserted: OurNode,
  ) {
    const parent = targetNode.parent;
    const neighborBelow = targetNode.neighborBelow;
    if (neighborBelow) {
      targetNode.neighborBelow = nodeToBeInserted;
      neighborBelow.neighborAbove = nodeToBeInserted;

      nodeToBeInserted.neighborAbove = targetNode;
      nodeToBeInserted.neighborBelow = neighborBelow;
    }

    nodeToBeInserted.parent = parent;

    const index = parent.children.indexOf(targetNode);
    parent.children.splice(index + 1, 0, nodeToBeInserted);

    return rootNode;
  }

  static insertInitialStructureNodeInMachineStructureFolderNode(
    rootNode: OurNode,
    targetNode: OurNode,
    droppedNode: OurNode,
  ) {
    if ("children" in droppedNode) {
      droppedNode.children = [];
    }

    if ("children" in targetNode) {
      targetNode.children.push(droppedNode);
    }

    return OurNode.childrenObjectTreeToFlatSet(rootNode);
  }

  static removeDraggedNodesFromPreviousLocationInMachineStructure(droppedNode) {
    // remove the node from the root
    const oldDroppedNode = droppedNode;
    const parent = oldDroppedNode?.parent;
    const neighborAbove = oldDroppedNode?.neighborAbove;
    const neighborBelow = oldDroppedNode?.neighborBelow;

    // update parent attribute on all children of the deleted node
    oldDroppedNode.children &&
      oldDroppedNode.children.forEach((child) => {
        child.parent = parent;
      });

    // update neighborAbove for the first child
    if (oldDroppedNode.children.length > 0) {
      oldDroppedNode.children[0].neighborAbove = neighborAbove
        ? neighborAbove
        : null;
    }

    // update neighborBelow for the last child
    if (oldDroppedNode.children.length > 0) {
      oldDroppedNode.children[
        oldDroppedNode.children.length - 1
      ].neighborBelow = neighborBelow ? neighborBelow : null;
    }

    if (oldDroppedNode.children.length > 0) {
      // update neighborAbove for the neighborBelow
      if (neighborBelow) {
        neighborBelow.neighborAbove =
          oldDroppedNode.children[oldDroppedNode.children.length - 1];
      }

      // update neighborBelow for the neighborAbove
      if (neighborAbove) {
        neighborAbove.neighborBelow = oldDroppedNode.children[0];
      }
    } else {
      // update nodes neighborAbove and neighborBelow neighbors
      if (neighborAbove) {
        oldDroppedNode.neighborAbove.neighborBelow = neighborBelow;
      }
      if (neighborBelow) {
        oldDroppedNode.neighborBelow.neighborAbove = neighborAbove;
      }
    }

    if (parent) {
      // find the index of the node in the parent's children array
      const nodeIndex = parent.children.indexOf(oldDroppedNode);

      // update the parents children
      parent.children.splice(nodeIndex, 1, ...oldDroppedNode.children);
    }

    // oldDroppedNode.parent = null;
  }

  static removeDeletedNodesFromPreviousLocationInMachineStructure(droppedNode) {
    const traverseParents = (node: OurNode) => {
      const parent = node.parent;

      if (parent) {
        // parent.id is checked because Cannot read properties of undefined (reading 'parent')
        // TypeError: Cannot read properties of undefined (reading 'parent') at countParentRecursionLevels
        // (webpack-internal:///./src/components/pages/flat-edit-structure-page/tree-components/tree-our-node/tree-view-array-from-map.tsx:43:19)
        // if (!parent.selected || parent.id === "ns=0;i=84") {
        if (
          !parent.selected ||
          parent.parent === undefined ||
          parent.parent === null
        ) {
          return parent;
        } else {
          traverseParents(parent);
        }
      } else {
        return undefined;
      }
    };

    // remove the node from the root
    const oldDroppedNode = droppedNode;
    const parent = traverseParents(oldDroppedNode);
    const neighborAbove = oldDroppedNode?.neighborAbove;
    const neighborBelow = oldDroppedNode?.neighborBelow;

    // update parent attribute on all children of the deleted node
    oldDroppedNode.children &&
      oldDroppedNode.children.forEach((child) => {
        child.parent = parent;
      });

    // update neighborAbove for the first child
    if (oldDroppedNode.children.length > 0) {
      oldDroppedNode.children[0].neighborAbove = neighborAbove
        ? neighborAbove
        : null;
    }

    // update neighborBelow for the last child
    if (oldDroppedNode.children.length > 0) {
      oldDroppedNode.children[
        oldDroppedNode.children.length - 1
      ].neighborBelow = neighborBelow ? neighborBelow : null;
    }

    if (oldDroppedNode.children.length > 0) {
      // update neighborAbove for the neighborBelow
      if (neighborBelow) {
        neighborBelow.neighborAbove =
          oldDroppedNode.children[oldDroppedNode.children.length - 1];
      }

      // update neighborBelow for the neighborAbove
      if (neighborAbove) {
        neighborAbove.neighborBelow = oldDroppedNode.children[0];
      }
    } else {
      // update nodes neighborAbove and neighborBelow neighbors
      if (neighborAbove) {
        oldDroppedNode.neighborAbove.neighborBelow = neighborBelow;
      }
      if (neighborBelow) {
        oldDroppedNode.neighborBelow.neighborAbove = neighborAbove;
      }
    }

    if (parent) {
      // find the index of the node in the parent's children array
      const nodeIndex = parent.children.indexOf(oldDroppedNode);

      // update the parents children
      parent.children.splice(nodeIndex, 1, ...oldDroppedNode.children);
    }

    // oldDroppedNode.parent = null;
  }

  static insertMachineStructureNodeInMachineStructureFolderNodeAndReturnFlatSet(
    rootNode: OurNode,
    targetNode: OurNode,
    droppedNode: OurNode,
  ) {
    // remove the node from the root
    OurNode.removeDraggedNodesFromPreviousLocationInMachineStructure(
      droppedNode,
    );

    if ("children" in droppedNode) {
      droppedNode.children = [];
    }

    if ("children" in targetNode) {
      targetNode.children.push(droppedNode);
    }

    return OurNode.childrenObjectTreeToFlatSet(rootNode);
  }

  static updateRelationshipsAndInsertNodesBetweenNodes(node, targetNode) {
    const parent = targetNode.parent;
    const neighborBelow = targetNode.neighborBelow;

    targetNode.neighborBelow = node;
    if (neighborBelow) {
      neighborBelow.neighborAbove = node;
    }
    node.neighborAbove = targetNode;
    node.neighborBelow = neighborBelow;
    if (parent) {
      OurNode.insertNodeAfterTargetNodeInChildrenArray(
        parent.children,
        targetNode,
        node,
      );
    } else {
      const newTargetNodeParent = targetNode.neighborAbove.parent;
      OurNode.insertNodeAfterTargetNodeInChildrenArray(
        newTargetNodeParent.children,
        targetNode,
        node,
      );
    }
  }

  // WARNING: all these methods work but might ignore the hierarchy of the dragged nodes - investigating
  static insertInitialStructureNodesBetweenMachineStructureNodes(
    rootNode: OurNode,
    targetNode: OurNode,
    draggedNodesList: OurNode[],
  ) {
    draggedNodesList.forEach((node, i) => {
      OurNode.updateRelationshipsAndInsertNodesBetweenNodes(
        node,
        draggedNodesList[i - 1] || targetNode,
      );
    });

    return OurNode.childrenObjectTreeToFlatSet(rootNode);
  }

  static insertMachineStructureNodesBetweenMachineStructureNodes(
    rootNode: OurNode,
    targetNode: OurNode,
    draggedNodesList: OurNode[],
  ) {
    // removes each node in the list from the previous location
    draggedNodesList.forEach((node) => {
      OurNode.removeDraggedNodesFromPreviousLocationInMachineStructure(node);
    });

    // adds each node in the list to the new location
    draggedNodesList.forEach((node, i) => {
      OurNode.updateRelationshipsAndInsertNodesBetweenNodes(
        node,
        draggedNodesList[i - 1] || targetNode,
      );
    });

    return OurNode.childrenObjectTreeToFlatSet(rootNode);
  }

  static insertMachineStructureNodesInMachineStructureFolder(
    rootNode: OurNode,
    targetNode: OurNode,
    draggedNodesList: OurNode[],
  ) {
    // removes each node in the list from the previous location
    draggedNodesList.forEach((node) => {
      OurNode.removeDraggedNodesFromPreviousLocationInMachineStructure(node);
    });

    // adds each node in the list in the target folder
    draggedNodesList.forEach((node, i) => {
      if ("children" in node) {
        node.children = [];
      }
      node.neighborAbove =
        targetNode.children[targetNode.children.length - 1] || null;
      node.neighborBelow = draggedNodesList[i + 1] || null;
      targetNode.children.push(node);
    });

    return OurNode.childrenObjectTreeToFlatSet(rootNode);
  }
  // nodes for drag and drop
  // insertNodeAfterTargetNodeInChildrenArray
  // appendFolderAfterNode
  // insertInitialStructureNodeBetweenMachineStructureNodes
  // insertInitialStructureNodeInMachineStructureFolderNode
  // removeDraggedNodesFromPreviousLocationInMachineStructure
  // insertMachineStructureNodeInMachineStructureFolderNode
  // updateRelationshipsAndInsertNodesBetweenNodes
  // insertInitialStructureNodesBetweenMachineStructureNodes
  // insertMachineStructureNodesBetweenMachineStructureNodes
  //
  // schritt 1: identifiziere die nodes die übertragen werden sollen und baue eine neue Struktur
  // schritt 2: entscheide ob die nodes entfernt werden soll in ihrer original location
  // schritt 3: appende die neue structure nach dem targetNode als Sybling
  // schritt 3b: appende die neue Structure ans Ende von einem Folder
  // schritt 4: returne eine neue objectstruktur
  //
  // (initialRootNode, machineRootNode, targetNode, structureToAppend) {
  //   (initialRootNode.reference ==== machineRootNode.reference) {
  //     // wenn beide das selbe Object sind, dann wissen wir wir müssen deleten
  //     initialRootNode.deleteNodes(structureToAppend);
  //   }
  //
  //   machineRootNode.appendNode(targetNode, structureToAppend);
  //   machineRootNode.insertNode(targetNode, structureToAppend);
  // }

  static createSelectedNodesStructureCopies = (node: OurNode): OurNode[] => {
    // const newNode = OurNode.createSelectedNodesStructureCopy(node);

    const result = [];

    if (node.selected) {
      result.push(OurNode.createSelectedNodesStructureCopy(node));
    } else {
      // return node.children.filter((child) => child.selected);
      node.children.forEach((child) => {
        result.push(OurNode.createSelectedNodesStructureCopy(child));
      });

      return result.filter((element) => element !== null);
    }
  };

  static createSelectedNodesStructureCopy = (
    node: OurNode,
    parent: OurNode | null = null,
  ): OurNode | null => {
    // create array for selected children
    const selectedChildren: OurNode[] = [];

    // Process children first
    for (const child of node.children) {
      const selectedChild = OurNode.createSelectedNodesStructureCopy(
        child,
        node,
      );

      if (selectedChild) {
        selectedChildren.push(selectedChild);
      }
    }

    if (node.selected) {
      const newNode = new OurNode({
        key: node.key,
        id: node.id,
        name: node.name,
        selected: node.selected,
        visible: node.visible,
        collapsed: node.collapsed,
        neighborAbove: null, // neighborAbove will be handled later
        neighborBelow: null, // neighborBelow will be handled later
        parent: parent, // set parent now
        updateChildren: false,
      });

      // Attach selected children to the new node
      for (const selectedChild of selectedChildren) {
        newNode.children.push(selectedChild);
      }

      // Handle neighbors within this node's children
      for (let i = 0; i < selectedChildren.length - 1; i++) {
        selectedChildren[i].neighborBelow = selectedChildren[i + 1];
        selectedChildren[i + 1].neighborAbove = selectedChildren[i];
      }

      return newNode;
    } else {
      // If the node is not selected, return the first selected child, or null if there are none
      if (selectedChildren.length > 0) {
        for (let i = 0; i < selectedChildren.length - 1; i++) {
          // set neigbor below
          selectedChildren[i].neighborBelow = selectedChildren[i + 1];

          // set neighbor above
          selectedChildren[i + 1].neighborAbove = selectedChildren[i];
        }

        // if (parent && parent.children.length > 0) {
        //   const parentIndex = parent.children.indexOf(node);

        //   if (parentIndex > 0) {
        //     selectedChildren[0].neighborAbove =
        //       parent.children[parentIndex - 1];

        //     parent.children[parentIndex - 1].neighborBelow =
        //       selectedChildren[0];
        //   }

        //   if (parentIndex < parent.children.length - 1) {
        //     selectedChildren[selectedChildren.length - 1].neighborBelow =
        //       parent.children[parentIndex + 1];

        //     parent.children[parentIndex + 1].neighborAbove =
        //       selectedChildren[selectedChildren.length - 1];
        //   }
        // }

        for (const selectedChild of selectedChildren) {
          selectedChild.parent = parent;
        }

        return selectedChildren[0];
      } else {
        return null;
      }
    }
  };

  static deleteStructureNodesFromBottomUp = (node: OurNode): void => {
    if (node.children.length > 0) {
      for (const child of node.children) {
        OurNode.deleteStructureNodesFromBottomUp(child);
      }
    }

    OurNode.removeDraggedNodesFromPreviousLocationInMachineStructure(node);
  };

  static removeSelectedNodesFromNode = (node: OurNode): OurNode => {
    if (node.children.length > 0) {
      for (const child of node.children) {
        OurNode.removeSelectedNodesFromNode(child);
      }
    }

    if (node.selected) {
      OurNode.removeDraggedNodesFromPreviousLocationInMachineStructure(node);
    }

    return node;
  };

  static deleteSelectedNodesFromNode = (node: OurNode): OurNode => {
    if (node.children.length > 0) {
      for (const child of node.children) {
        OurNode.deleteSelectedNodesFromNode(child);
      }
    }

    if (node.selected) {
      OurNode.removeDeletedNodesFromPreviousLocationInMachineStructure(node);
    }

    return node;
  };

  static dragAndDrop = (
    sourceRootNode,
    targetRootNode,
    targetNode,
  ): { sourceRootNode: OurNode; targetRootNode: OurNode } => {
    const newStructures =
      OurNode.createSelectedNodesStructureCopies(sourceRootNode);

    console.log("newStructures", newStructures);

    if (sourceRootNode === targetRootNode) {
      console.log("ist gleich");
      OurNode.removeSelectedNodesFromNode(targetRootNode);
    } else {
      console.log("ist nicht gleich");
    }

    for (let i = 0; i < newStructures.length; i++) {
      if (i === 0) {
        OurNode.insertNodeInChildrensArray(
          targetRootNode,
          targetNode,
          newStructures[i],
        );
      } else {
        OurNode.insertNodeInChildrensArray(
          targetRootNode,
          // targetNode,
          newStructures[i - 1],
          newStructures[i],
        );
      }
    }

    return { sourceRootNode, targetRootNode };
  };

  toggleNodeTransfered() {
    this.transfered = !this.transfered;
  }

  createAncestorsList(): string[] {
    const traverseParents = (node: OurNode, ancestors: string[]) => {
      if (node.parent) {
        ancestors.unshift(node.parent.name);
        traverseParents(node.parent, ancestors);
      }
    };

    const ancestors: string[] = [];
    traverseParents(this, ancestors);
    ancestors.push(this.name);
    return ancestors;
  }

  createIdsArrayFromOurNodeTree() {
    const idsArray = [];

    const traverseAndMap = (node) => {
      idsArray.push(node.id);
      node.children.forEach(traverseAndMap);
    };

    traverseAndMap(this);
    return idsArray;
  }

  static makeObjectTree = (node: any): OurNode => {
    const ourNodeBuilder = (
      node: any,
      parent: OurNode | null = null,
    ): OurNode => {
      return new OurNode({
        key: node.key,
        id: node.id,
        name: node.name,
        node_type: node.node_type,
        source_type: node.source_type,
        attributes: node.attributes, // TODO: check if it is a reference or a copy
        selected: node.selected,
        visible: node.visible,
        collapsed: node.collapsed,
        transfered: node.transfered,
        // neighborAbove: node.neighborAbove,
        // neighborBelow: node.neighborBelow,
        parent: parent,
        updateChildren: false,
      });
    };

    const traverse = (node: any, parent: OurNode | null = null): OurNode => {
      const newNode = ourNodeBuilder(node, parent);

      if (node.children) {
        node.children.forEach((child: any) => {
          newNode.children.push(traverse(child, newNode));
        });

        // update neighbor relations
        node.children.forEach((child: any, index: number) => {
          if (index > 0) {
            newNode.children[index].neighborAbove = newNode.children[index - 1];
          }
          if (index < node.children.length - 1) {
            newNode.children[index].neighborBelow = newNode.children[index + 1];
          }
        });
      }

      return newNode;
    };

    return traverse(node);
  };

  static makeObjectFlatMap(object: any): Map<string, OurNode> {
    const flatMap: Map<string, OurNode> = new Map();
    let lastNodeAtSameLevel: OurNode | null = null;

    function traverse(node: any, parent: OurNode | null): void {
      const newNode = new OurNode({
        key: node.key,
        id: node.id,
        name: node.name,
        node_type: node.node_type,
        attributes: node.attributes,
        selected: node.selected,
        visible: node.visible,
        collapsed: node.collapsed,
        transfered: node.transfered,
        parent: parent,
        updateChildren: false,
      });
      flatMap.set(newNode.id, newNode);

      if (lastNodeAtSameLevel) {
        lastNodeAtSameLevel.neighborBelow = newNode;
        newNode.neighborAbove = lastNodeAtSameLevel;
      }
      lastNodeAtSameLevel = newNode;

      const previousNodeAtThisLevel = lastNodeAtSameLevel;

      if (node.children && node.children.length > 0) {
        node.children.forEach((child: any, index: number) => {
          if (index === 0) {
            lastNodeAtSameLevel = null;
          }
          traverse(child, newNode);
        });
      }

      // Restore last node for siblings level
      lastNodeAtSameLevel = previousNodeAtThisLevel;
    }

    traverse(object, null);
    return flatMap;
  }

  createMapFromOurNodeTree() {
    const nodeMap = new Map();

    const traverseAndMap = (node) => {
      nodeMap.set(node.id, node);
      node.children.forEach(traverseAndMap);
    };

    traverseAndMap(this);
    return nodeMap;
  }

  // -------------NEW DRAG AND DROP METHODS-------------------------------------

  static removeSelectedNodesFromNodeNew = (node: OurNode): OurNode => {
    // Use a queue to gather nodes to be removed
    const nodesToRemove = [];

    // Inner function to collect nodes that are selected
    const collectSelectedNodes = (node: OurNode) => {
      if (node.children.length > 0) {
        for (const child of node.children) {
          collectSelectedNodes(child);
        }
      }

      if (node.selected) {
        nodesToRemove.push(node);
      }
    };

    // Collect all selected nodes
    collectSelectedNodes(node);

    // Remove collected nodes
    for (const node of nodesToRemove) {
      OurNode.removeDraggedNodesFromPreviousLocationInMachineStructure(node);
    }

    return node;
  };

  static createSelectedNodesStructure = (
    node: OurNode,
    parent: OurNode | null = null,
    toggleTransferedAttribute = false,
  ): OurNode[] | null => {
    // create array for selected children
    const selectedChildren: OurNode[] = [];

    // Process children first
    for (const child of node.children) {
      const selectedChild = OurNode.createSelectedNodesStructure(
        child,
        node,
        toggleTransferedAttribute,
      );

      if (selectedChild) {
        selectedChildren.push(...selectedChild);
      }
    }

    if (node.selected) {
      const newNode = new OurNode({
        key: node.key,
        id: node.id,
        name: node.name,
        node_type: node.node_type,
        selected: node.selected,
        visible: node.visible,
        collapsed: node.collapsed,
        attributes: node.attributes,
        transfered: toggleTransferedAttribute
          ? !node.transfered
          : node.transfered,
        neighborAbove: null, // neighborAbove will be handled later
        neighborBelow: null, // neighborBelow will be handled later
        parent: node.parent, // set parent now
        updateChildren: false,
      });

      // Attach selected children to the new node
      for (const selectedChild of selectedChildren) {
        newNode.children.push(selectedChild);
      }

      // Handle neighbors within this node's children
      for (let i = 0; i < selectedChildren.length - 1; i++) {
        selectedChildren[i].neighborBelow = selectedChildren[i + 1];
        selectedChildren[i + 1].neighborAbove = selectedChildren[i];
      }

      return [newNode];
    } else {
      // If the node is not selected, return the first selected child, or null if there are none
      if (selectedChildren.length > 0) {
        for (let i = 0; i < selectedChildren.length - 1; i++) {
          // set neigbor below
          selectedChildren[i].neighborBelow = selectedChildren[i + 1];

          // set neighbor above
          selectedChildren[i + 1].neighborAbove = selectedChildren[i];
        }

        // if (parent && parent.children.length > 0) {
        //   const parentIndex = parent.children.indexOf(node);

        //   if (parentIndex > 0) {
        //     selectedChildren[0].neighborAbove =
        //       parent.children[parentIndex - 1];

        //     parent.children[parentIndex - 1].neighborBelow =
        //       selectedChildren[0];
        //   }

        //   if (parentIndex < parent.children.length - 1) {
        //     selectedChildren[selectedChildren.length - 1].neighborBelow =
        //       parent.children[parentIndex + 1];

        //     parent.children[parentIndex + 1].neighborAbove =
        //       selectedChildren[selectedChildren.length - 1];
        //   }
        // }

        for (const selectedChild of selectedChildren) {
          selectedChild.parent = parent;
        }

        return selectedChildren;
      } else {
        return null;
      }
    }
  };

  static buildSelectedNodesStructureConsideringTargetContext = (
    root: OurNode,
    targetNode: OurNode,
    toggleTransferedAttribute = false,
  ): OurNode[] => {
    (OurNode as any)._belowSelectedStructure = [];

    const above = OurNode.createSelectedNodesStructureDependingOnTargetNode(
      root,
      null,
      targetNode,
      toggleTransferedAttribute,
    );

    const below = (OurNode as any)._belowSelectedStructure;

    const finalResult: OurNode[] = [];
    if (above) finalResult.push(...above);
    if (below && below.length > 0) finalResult.push(...below);

    // Cleanup
    delete (OurNode as any)._belowSelectedStructure;

    return finalResult;
  };

  static createSelectedNodesStructureDependingOnTargetNode = (
    node: OurNode,
    parent: OurNode | null = null,
    targetNode: OurNode,
    toggleTransferedAttribute = false,
    _isUnderTarget = false, // internal helper
  ): OurNode[] | null => {
    // If we hit the targetNode, stop traversal for the 'above' structure
    if (node.id === targetNode.id && !_isUnderTarget) {
      // Build the "below" part starting from targetNode separately
      const belowStructure =
        OurNode.createSelectedNodesStructureDependingOnTargetNode(
          node,
          parent,
          targetNode,
          toggleTransferedAttribute,
          true,
        );
      // Save it into a static temp variable or closure
      (OurNode as any)._belowSelectedStructure = belowStructure || [];
      return null; // Exclude this node from 'above' structure
    }

    const selectedChildren: OurNode[] = [];

    for (const child of node.children) {
      const result = OurNode.createSelectedNodesStructureDependingOnTargetNode(
        child,
        node,
        targetNode,
        toggleTransferedAttribute,
        _isUnderTarget,
      );
      if (result) {
        selectedChildren.push(...result);
      }
    }

    if (node.selected) {
      const newNode = new OurNode({
        key: node.key,
        id: node.id,
        name: node.name,
        node_type: node.node_type,
        selected: node.selected,
        visible: node.visible,
        collapsed: node.collapsed,
        attributes: node.attributes,
        transfered: toggleTransferedAttribute
          ? !node.transfered
          : node.transfered,
        neighborAbove: null,
        neighborBelow: null,
        parent: parent,
        updateChildren: false,
      });

      for (const selectedChild of selectedChildren) {
        newNode.children.push(selectedChild);
      }

      // Set neighbors
      for (let i = 0; i < selectedChildren.length - 1; i++) {
        selectedChildren[i].neighborBelow = selectedChildren[i + 1];
        selectedChildren[i + 1].neighborAbove = selectedChildren[i];
      }

      return [newNode];
    } else {
      if (selectedChildren.length > 0) {
        for (let i = 0; i < selectedChildren.length - 1; i++) {
          selectedChildren[i].neighborBelow = selectedChildren[i + 1];
          selectedChildren[i + 1].neighborAbove = selectedChildren[i];
        }

        for (const selectedChild of selectedChildren) {
          selectedChild.parent = parent;
        }

        return selectedChildren;
      } else {
        return null;
      }
    }
  };

  static addSelectedNodesToChildrenArrayOfImaginaryRootNode(
    node,
    toggleTransferedAttribute = false,
  ): OurNode {
    const newRootNode = new OurNode({
      key: node.key,
      id: node.id,
      name: "imaginary-root",
      node_type: node.node_type,
      attributes: node.attributes,
      selected: false,
      visible: node.visible,
      collapsed: node.collapsed,
      transfered: toggleTransferedAttribute,
      parent: null,
      updateChildren: false,
    });

    const result = OurNode.createSelectedNodesStructure(
      node,
      null,
      toggleTransferedAttribute,
    );

    if (result) {
      result.forEach((child) => {
        child.parent = newRootNode;
      });

      newRootNode.children = result;
      return newRootNode;
    }

    return undefined;
  }

  static addSelectedNodesToChildrenArrayOfImaginaryRootNodeConsideringTargetContext(
    node,
    targetNode: OurNode,
    toggleTransferedAttribute = false,
  ): OurNode {
    const newRootNode = new OurNode({
      key: node.key,
      id: node.id,
      name: "imaginary-root",
      node_type: node.node_type,
      attributes: node.attributes,
      selected: false,
      visible: node.visible,
      collapsed: node.collapsed,
      transfered: toggleTransferedAttribute,
      parent: null,
      updateChildren: false,
    });

    const result = OurNode.buildSelectedNodesStructureConsideringTargetContext(
      node,
      targetNode,
      toggleTransferedAttribute,
    );

    if (result) {
      result.forEach((child) => {
        child.parent = newRootNode;
      });

      newRootNode.children = result;
      return newRootNode;
    }

    return undefined;
  }

  static updateParents(node, newParentNode) {
    node.parent = newParentNode;

    node.children.forEach((child) => {
      OurNode.updateParents(child, node);
    });
  }

  insertStructureArrayAfterTargetNodeIdInParentChildrenArray(
    newStructureArray: OurNode[],
  ): OurNode[] {
    const targetsNeighborBelow: OurNode = this.neighborBelow;
    const firstElementOfNewStructure: OurNode = newStructureArray[0];
    const lastElementOfNewStructure: OurNode =
      newStructureArray[newStructureArray.length - 1];

    // update the the targets neighborBelow to be the first element of the new structure
    this.neighborBelow = firstElementOfNewStructure;

    // update the first element of the new structure to have the target as the neighbor above
    firstElementOfNewStructure.neighborAbove = this;

    if (targetsNeighborBelow) {
      // update neigborabove of the targets sibling to be the last element of the new structure
      targetsNeighborBelow.neighborAbove = lastElementOfNewStructure;

      // update neighborbelow of the last element of the new structure to be the targets sibling
      lastElementOfNewStructure.neighborBelow = targetsNeighborBelow;
    }

    // find the index in the array that should have the new structure inserted after
    const index: number = this.parent.children.findIndex(
      (element) => element.id === this.id,
    );

    this.parent.children.splice(index + 1, 0, ...newStructureArray);
    return this.parent.children;
  }

  static insertInitialStructureNodesInMachineStructureBetweenNodesAndReturnMachineStructure(
    initialStructure: OurNode,
    targetNode: OurNode,
  ) {
    // this is to make sure, that when rootNode is selected in the application
    // it does not get dropped in machine structure
    initialStructure.selected = false;

    const newStructure =
      OurNode.addSelectedNodesToChildrenArrayOfImaginaryRootNode(
        initialStructure,
      );

    if (newStructure) {
      newStructure.children.forEach((child) => {
        this.updateParents(child, targetNode.parent);
      });

      const newConcatenatedArray =
        targetNode.insertStructureArrayAfterTargetNodeIdInParentChildrenArray(
          newStructure.children,
        );

      targetNode.parent.children = newConcatenatedArray;
    }

    const newMachineStructure = targetNode.returnRootNodeFromOurNode();

    return newMachineStructure;
  }

  static insertInitialStructureNodesInMachineStructureFolderNodeAndReturnMachineStructure(
    initialStructure: OurNode,
    targetNode: OurNode,
  ) {
    // this is to make sure, that when rootNode is selected in the application
    // it does not get dropped in machine structure
    initialStructure.selected = false;

    const newStructure =
      OurNode.addSelectedNodesToChildrenArrayOfImaginaryRootNode(
        initialStructure,
      );

    if (newStructure) {
      newStructure.children.forEach((child) => {
        this.updateParents(child, targetNode);
      });

      if (targetNode.collapsed !== newStructure.collapsed) {
        newStructure.toggleNodeCollapse();
        newStructure.visible = !targetNode.visible;
      }

      const newConcatenatedArray = targetNode.children.concat(
        newStructure.children,
      );

      targetNode.children = newConcatenatedArray;
    }

    const newMachineStructure = targetNode.returnRootNodeFromOurNode();

    return newMachineStructure;
  }

  static insertMachineStructureNodesInMachineStructureBetweenNodesAndReturnMachineStructure(
    machineStructure: OurNode,
    targetNode: OurNode,
  ) {
    const newStructure =
      OurNode.addSelectedNodesToChildrenArrayOfImaginaryRootNode(
        machineStructure,
      );

    if (newStructure) {
      newStructure.children.forEach((child) => {
        if (targetNode.parent.id === child.id) {
          this.updateParents(child, targetNode.parent.parent);
        } else {
          this.updateParents(child, targetNode.parent);
        }
      });

      if (!newStructure.createMapFromOurNodeTree().get(targetNode.id)) {
        OurNode.removeSelectedNodesFromNodeNew(machineStructure);

        const newConcatenatedArray =
          targetNode.insertStructureArrayAfterTargetNodeIdInParentChildrenArray(
            newStructure.children,
          );

        targetNode.parent.children = newConcatenatedArray;
      }
    }
    const newMachineStructure = targetNode.returnRootNodeFromOurNode();

    return newMachineStructure;
  }

  static insertMachineStructureNodesInMachineStructureFolderNodeAndReturnMachineStructure(
    machineStructure: OurNode,
    targetNode: OurNode,
  ) {
    const newStructure =
      OurNode.addSelectedNodesToChildrenArrayOfImaginaryRootNodeConsideringTargetContext(
        machineStructure,
        copy(targetNode),
      );

    if (newStructure) {
      newStructure.children.forEach((child) => {
        this.updateParents(child, targetNode);
      });

      if (targetNode.collapsed !== newStructure.collapsed) {
        newStructure.toggleNodeCollapse();
        newStructure.visible = !targetNode.visible;
      }

      // abort if the target node is in the newStructure
      if (!newStructure.createMapFromOurNodeTree().get(targetNode.id)) {
        let newConcatenatedArray = [];

        const getDuplicateIdsBetweenNewStructureAndTargetNodeChildren = (
          childrenOfNewStructure,
          childrenOfTargetNode,
        ) => {
          const idSet = new Set(childrenOfTargetNode.map((obj) => obj.id));
          return childrenOfNewStructure
            .filter((item) => idSet.has(item.id))
            .map((item) => item.id);
        };

        const duplicateIds =
          getDuplicateIdsBetweenNewStructureAndTargetNodeChildren(
            newStructure.children,
            targetNode.children,
          );

        if (duplicateIds.length > 0) {
          //TODO: implement this ONLY if everything is working
          const updatedItems = newStructure.children.filter(
            (item) => !duplicateIds.includes(item.id),
          );

          const removeNodesByIdInPlace = (node, nodesToRemove) => {
            const idsToRemove = new Set(nodesToRemove.map((n) => n.id));

            const prune = (currentNode) => {
              if (!currentNode.children) return;

              // Remove children with matching IDs
              currentNode.children = currentNode.children.filter((child) => {
                return !idsToRemove.has(child.id);
              });

              // Recurse into remaining children
              for (const child of currentNode.children) {
                prune(child);
              }
            };

            prune(node);
          };

          removeNodesByIdInPlace(targetNode, updatedItems);

          newConcatenatedArray = [...targetNode.children, ...updatedItems];

          targetNode.deselectYourselfAndAllChildren();

          OurNode.removeSelectedNodesFromNodeNew(machineStructure);

          //TODO: implement this if there is no better solution
          /* newConcatenatedArray = targetNode.children; */
          /* warningSnackbar(
            "You cannot drop a node into itself. Last drop was ignored. Please check your selection and try again.",
            5000,
          ); */
        } else {
          newConcatenatedArray = [
            // Keep unselected children from targetNode
            ...targetNode.children.filter(
              (existingChild) =>
                !existingChild.selected &&
                !newStructure.children.some(
                  (newChild) => newChild.id === existingChild.id,
                ),
            ),
            // Add all children from newStructure
            ...newStructure.children,
          ];
          OurNode.removeSelectedNodesFromNodeNew(machineStructure);
        }

        targetNode.children = newConcatenatedArray;
      }
    }
    const newMachineStructure = targetNode.returnRootNodeFromOurNode();

    return newMachineStructure;
  }
}
